// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Latency calculation and connectivity status helpers.
 *
 * Extracted from gameStore to keep latency logic testable
 * and the hysteresis thresholds clearly documented.
 */

/** Maximum number of RTT samples to keep. */
export const MAX_LATENCY_SAMPLES = 60;

/** Hysteresis thresholds (ms) to prevent status flapping. */
const YELLOW_UP = 120;
const YELLOW_DOWN = 80;
const BLUE_UP = 330;
const BLUE_DOWN = 270;

export type ConnectivityStatus = 'green' | 'blue' | 'yellow' | 'red';

/**
 * Compute a new connectivity status using hysteresis.
 *
 * Hysteresis prevents rapid status changes when latency hovers
 * near a threshold boundary — the "up" threshold to degrade is
 * higher than the "down" threshold to recover.
 */
export function computeConnectivityStatus(
  averageLatency: number,
  currentStatus: ConnectivityStatus,
  isConnected: boolean
): ConnectivityStatus {
  if (!isConnected) return 'red';

  if (currentStatus === 'green') {
    return averageLatency > YELLOW_UP ? 'yellow' : 'green';
  }
  if (currentStatus === 'yellow') {
    if (averageLatency > BLUE_UP) return 'blue';
    if (averageLatency < YELLOW_DOWN) return 'green';
    return 'yellow';
  }
  if (currentStatus === 'blue') {
    return averageLatency < BLUE_DOWN ? 'yellow' : 'blue';
  }
  // From red — apply fresh thresholds
  if (averageLatency > BLUE_UP) return 'blue';
  if (averageLatency > YELLOW_UP) return 'yellow';
  return 'green';
}

/** Round a number to 3 decimal places. */
export function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Compute the average of an array, rounded to 3 decimals. */
export function computeAverage(history: number[]): number {
  if (history.length === 0) return 0;
  return round3(history.reduce((a, b) => a + b, 0) / history.length);
}

/** Compute a latency percentile (e.g. P95). */
export function computePercentile(history: number[], percentile: number): number {
  if (history.length === 0) return 0;
  const sorted = [...history].sort((a, b) => a - b);
  const index = Math.ceil((percentile / 100) * sorted.length) - 1;
  return round3(sorted[Math.max(0, index)] || 0);
}
